import React, { Component } from "react";
import axios from "axios";
import Helmet from "react-helmet";
import { apiHost } from "../../api/apiClient";
import { utcToLocal } from "../../utils/dateConvert";
import "../../styles/maintenance/maintenanceSubscribers.scss";

class MaintenanceSubscribers extends Component {
  state = {
    rows: [],
    search: "",
    status: "all",
    isLoading: false,
    isRunning: false,
    updatingUserId: "",
    error: "",
    message: "",
  };

  componentDidMount() {
    this.loadSubscriptions();
  }

  loadSubscriptions = async () => {
    this.setState({ isLoading: true, error: "" });

    try {
      const response = await axios.get(`${apiHost}/api/admin/maintenance-subscriptions`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });

      this.setState({
        rows: Array.isArray(response.data) ? response.data : [],
        isLoading: false,
        error: Array.isArray(response.data) ? "" : "The subscriptions API returned an unexpected response.",
      });
    } catch (error) {
      const status = error && error.response ? error.response.status : "";
      this.setState({
        rows: [],
        isLoading: false,
        error: `Unable to load maintenance subscriptions.${status ? ` Status: ${status}.` : ""}`,
      });
    }
  };

  getFilteredRows = () => {
    const search = this.state.search.trim().toLowerCase();

    return this.state.rows.filter((row) => {
      if (this.state.status === "subscribed" && !row.subscribed) return false;
      if (this.state.status === "unsubscribed" && row.subscribed) return false;
      if (!search) return true;

      return [row.email, row.userName, row.displayName, row.customerName, row.userId]
        .some((value) => (value || "").toString().toLowerCase().includes(search));
    });
  };

  unsubscribe = async (row) => {
    const identity = row.email || row.userName || row.userId;
    if (!window.confirm(`Unsubscribe ${identity} from weekly maintenance email alerts?`)) {
      return;
    }

    this.setState({ updatingUserId: row.userId, error: "" });

    try {
      const response = await axios.put(
        `${apiHost}/api/admin/maintenance-subscriptions/${encodeURIComponent(row.userId)}/unsubscribe`,
        {},
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      const updatedAt = response.data && response.data.updatedAt
        ? response.data.updatedAt
        : new Date().toISOString();

      this.setState((state) => ({
        rows: state.rows.map((item) => item.userId === row.userId
          ? { ...item, subscribed: false, updatedAt }
          : item),
        updatingUserId: "",
      }));
    } catch (error) {
      const status = error && error.response ? error.response.status : "";
      this.setState({
        updatingUserId: "",
        error: `Unable to unsubscribe ${identity}.${status ? ` Status: ${status}.` : ""}`,
      });
    }
  };

  runNow = async () => {
    if (!window.confirm("Run the weekly maintenance email job now? Eligible subscribed users may receive an email.")) {
      return;
    }

    this.setState({ isRunning: true, error: "", message: "" });
    try {
      const response = await axios.post(
        `${apiHost}/api/admin/maintenance-subscriptions/run-now`,
        {},
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      this.setState({
        isRunning: false,
        message: response.data && response.data.message
          ? response.data.message
          : "The maintenance email job has been queued.",
      });
    } catch (error) {
      const response = error && error.response;
      const responseMessage = response && response.data && response.data.message;
      this.setState({
        isRunning: false,
        error: responseMessage || `Unable to run the maintenance email job.${response && response.status ? ` Status: ${response.status}.` : ""}`,
      });
    }
  };

  render() {
    const { rows, isLoading, isRunning, updatingUserId, error, message, search, status } = this.state;
    const filteredRows = this.getFilteredRows();
    const subscribedCount = rows.filter((row) => row.subscribed).length;
    const unsubscribedCount = rows.length - subscribedCount;

    return (
      <div className="contentWrapper contentWrapperSmoothRelease maintenance-subscribers-page">
        <Helmet>
          <title>Maintenance Subscribers | VisionTrack</title>
        </Helmet>

        <section className="maintenance-subscribers-header">
          <div>
            <h1>Maintenance Email Subscribers</h1>
            <p>Users who have configured weekly Maintenance Due email alerts.</p>
          </div>
          <div className="maintenance-subscribers-header-actions">
            <button className="run-now" type="button" onClick={this.runNow} disabled={isRunning}>
              {isRunning ? "Running..." : "Run emails now"}
            </button>
            <button type="button" onClick={this.loadSubscriptions} disabled={isLoading}>
              {isLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </section>

        <section className="maintenance-subscribers-filters">
          <label>
            <span>Search</span>
            <input
              value={search}
              onChange={(event) => this.setState({ search: event.target.value })}
              placeholder="Email, username, customer or user ID"
            />
          </label>
          <div className="maintenance-subscriber-status-filter">
            <span>Status</span>
            <div>
              <button className={status === "all" ? "active" : ""} onClick={() => this.setState({ status: "all" })}>
                All <b>{rows.length}</b>
              </button>
              <button className={status === "subscribed" ? "active" : ""} onClick={() => this.setState({ status: "subscribed" })}>
                Subscribed <b>{subscribedCount}</b>
              </button>
              <button className={status === "unsubscribed" ? "active" : ""} onClick={() => this.setState({ status: "unsubscribed" })}>
                Unsubscribed <b>{unsubscribedCount}</b>
              </button>
            </div>
          </div>
        </section>

        {error && <div className="maintenance-subscribers-error">{error}</div>}
        {message && <div className="maintenance-subscribers-message">{message}</div>}

        <section className="maintenance-subscribers-table-card">
          <div className="maintenance-subscribers-table-summary">
            <strong>{filteredRows.length.toLocaleString()} users</strong>
            <span>Subscription settings are controlled by each user from Maintenance Due.</span>
          </div>
          <div className="maintenance-subscribers-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Display name</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Date threshold</th>
                  <th>KM threshold</th>
                  <th>Updated</th>
                  <th>User ID</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && filteredRows.map((row) => (
                  <tr key={row.userId}>
                    <td className="maintenance-subscriber-email">{row.email || "—"}</td>
                    <td>{row.userName || "—"}</td>
                    <td>{row.displayName || "—"}</td>
                    <td>{row.customerName || "—"}</td>
                    <td>
                      <span className={`maintenance-subscriber-badge ${row.subscribed ? "subscribed" : "unsubscribed"}`}>
                        {row.subscribed ? "Subscribed" : "Unsubscribed"}
                      </span>
                    </td>
                    <td>{Number(row.dateThresholdDays || 0).toLocaleString()} days</td>
                    <td>{Number(row.kmThreshold || 0).toLocaleString()} km</td>
                    <td>{row.updatedAt ? utcToLocal(row.updatedAt) : "—"}</td>
                    <td className="maintenance-subscriber-id" title={row.userId}>{row.userId}</td>
                    <td>
                      {row.subscribed ? (
                        <button
                          type="button"
                          className="maintenance-subscriber-unsubscribe"
                          onClick={() => this.unsubscribe(row)}
                          disabled={updatingUserId === row.userId}
                        >
                          {updatingUserId === row.userId ? "Unsubscribing…" : "Unsubscribe"}
                        </button>
                      ) : (
                        <span className="maintenance-subscriber-no-action">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!isLoading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan="10" className="maintenance-subscribers-empty">No matching subscription settings found.</td>
                  </tr>
                )}
                {isLoading && (
                  <tr>
                    <td colSpan="10" className="maintenance-subscribers-empty">Loading subscriptions…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }
}

export default MaintenanceSubscribers;
